import React, { useState } from "react";
import { Check, Copy, ExternalLink, MessageSquare, Phone } from "lucide-react";
import { toast } from "sonner";
import { OtcRequestItem } from "../../../otc-requests/types";
import { CustomerAvatar } from "../../../otc-requests/components/CustomerAvatar";
import { CurrencyFlag } from "./CurrencyFlag";

export interface OtcCustomerDetailsColProps {
  request: OtcRequestItem;
  onViewWallet?: () => void;
}

const isDisplayable = (val?: unknown): boolean => {
  if (val === null || val === undefined) return false;
  if (typeof val === "object") return false;
  const str = String(val).trim();
  return (
    str !== "" &&
    str !== "-" &&
    str !== "N/A" &&
    str !== "[]" &&
    str !== "{}" &&
    str !== "[object Object]" &&
    str !== "null" &&
    str !== "undefined"
  );
};

export const OtcCustomerDetailsCol: React.FC<OtcCustomerDetailsColProps> = ({
  request,
  onViewWallet,
}) => {
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const handleCopy = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopiedKey(null), 1500);
  };

  const customerName = isDisplayable(request.customer?.name)
    ? String(request.customer?.name)
    : "Customer";
  const customerEmail = isDisplayable(request.customer?.email)
    ? String(request.customer?.email)
    : "-";
  const currency = (request.currency || "USD").toUpperCase();

  const refCode = isDisplayable(request.reference)
    ? String(request.reference)
    : isDisplayable(request.transactionId)
      ? String(request.transactionId)
      : String(request.id || "-");
  const settlementMethod = isDisplayable(request.settlementMethod)
    ? String(request.settlementMethod)
    : "-";

  const recipientName = isDisplayable(request.recipientDetails?.name)
    ? String(request.recipientDetails?.name)
    : "-";
  const recipientAccount = isDisplayable(request.recipientDetails?.accountNumber)
    ? String(request.recipientDetails?.accountNumber)
    : "";
  const recipientBank = isDisplayable(request.recipientDetails?.bankName)
    ? String(request.recipientDetails?.bankName)
    : "";
  const swiftCode = isDisplayable(request.recipientDetails?.swiftCode)
    ? String(request.recipientDetails?.swiftCode)
    : "";
  const routingNumber = isDisplayable(request.recipientDetails?.routingNumber)
    ? String(request.recipientDetails?.routingNumber)
    : "";
  const iban = isDisplayable(request?.iban) ? String(request?.iban) : "";
  const paymentProvider = isDisplayable(request?.paymentProvider)
    ? String(request?.paymentProvider)
    : "";

  const hasAccount = Boolean(recipientAccount);
  const hasBank = Boolean(recipientBank);
  const recipientSubtext =
    hasAccount && hasBank
      ? `${recipientAccount} - ${recipientBank}`
      : hasAccount
        ? recipientAccount
        : hasBank
          ? recipientBank
          : "";

  return (
    <div className="flex flex-col lg:pr-4">
      {/* Customer info header with action icons from Figma */}
      <div className="flex h-[40px] items-center justify-between">
        <div className="flex min-w-0 items-center gap-3">
          <CustomerAvatar
            name={customerName}
            pattern={request.customer?.avatarPattern}
            size={36}
          />
          <div className="flex min-w-0 flex-col">
            <span
              className="truncate text-[14px] font-semibold text-[#101828]"
              title={customerName}
            >
              {customerName}
            </span>
            <span
              className="truncate text-[12px] text-[#667085]"
              title={customerEmail}
            >
              {customerEmail}
            </span>
          </div>
        </div>

        {/* Customer quick action buttons from Figma */}
        <div className="flex items-center gap-1 text-[#667085]">
          <button
            type="button"
            onClick={() => {
              if (request.customer?.phone) {
                window.open(`tel:${request.customer.phone}`);
              } else {
                toast.info(`Calling ${customerName}...`);
              }
            }}
            className="rounded-lg p-1.5 transition-colors hover:bg-[#F2F4F7] hover:text-[#101828]"
            title="Call customer"
          >
            <Phone size={14} />
          </button>
          <button
            type="button"
            onClick={() => toast.info(`Starting chat with ${customerName}`)}
            className="rounded-lg p-1.5 transition-colors hover:bg-[#F2F4F7] hover:text-[#101828]"
            title="Message customer"
          >
            <MessageSquare size={14} />
          </button>
          <button
            type="button"
            onClick={() => toast.info(`Opening profile for ${customerName}`)}
            className="rounded-lg p-1.5 transition-colors hover:bg-[#F2F4F7] hover:text-[#101828]"
            title="View customer profile"
          >
            <ExternalLink size={14} />
          </button>
        </div>
      </div>

      {/* Wallet balance with border top AND bottom */}
      <div className="my-3.5 flex items-center justify-between border-y border-[#F2F4F7] py-3.5 text-[13px]">
        <div className="flex items-center gap-2 text-[#475467]">
          <CurrencyFlag currency={currency} />
          <span>Wallet balance</span>
        </div>
        <div className="flex items-center gap-1.5 font-semibold text-[#101828]">
          <span>{request.customer?.walletBalance || "-"}</span>
          <button
            type="button"
            onClick={onViewWallet || (() => toast.info("Opening customer wallet ledger"))}
            className="rounded p-0.5 text-[#6F00FF] transition-colors hover:text-[#5800CC]"
            title="View wallet"
          >
            <ExternalLink size={13} />
          </button>
        </div>
      </div>

      {/* Detail rows */}
      <div className="flex flex-col gap-4 text-[13px]">
        {/* Reference */}
        <div className="flex items-center justify-between">
          <span className="text-[#667085]">Reference</span>
          <div className="flex items-center gap-1.5 font-medium text-[#101828]">
            <span
              className="max-w-[140px] truncate font-mono text-[12px] sm:max-w-[180px]"
              title={refCode}
            >
              {refCode}
            </span>
            <button
              type="button"
              onClick={() => handleCopy(refCode, "ref")}
              className="rounded p-0.5 text-[#667085] transition-colors hover:text-[#101828]"
              title="Copy reference"
            >
              {copiedKey === "ref" ? (
                <Check size={13} className="text-[#12B76A]" />
              ) : (
                <Copy size={13} />
              )}
            </button>
          </div>
        </div>

        {/* Method */}
        <div className="flex items-center justify-between">
          <span className="text-[#667085]">Settlement method</span>
          <span
            className="max-w-[180px] truncate font-medium text-[#101828]"
            title={settlementMethod}
          >
            {settlementMethod}
          </span>
        </div>

        {/* Recipient */}
        <div className="flex items-start justify-between">
          <span className="text-[#667085]">Recipient</span>
          <div className="flex flex-col items-end text-right">
            <span
              className="max-w-[160px] truncate font-medium text-[#101828]"
              title={recipientName}
            >
              {recipientName}
            </span>
            {recipientSubtext && (
              <span
                className="max-w-[180px] truncate text-[12px] text-[#667085]"
                title={recipientSubtext}
              >
                {recipientSubtext}
              </span>
            )}
          </div>
        </div>

        {/* Payment Method */}
        {Boolean(paymentProvider) && (
          <div className="flex items-center justify-between">
            <span className="text-[#667085]">Payment Method</span>
            <span className="font-medium text-[#101828]">{paymentProvider}</span>
          </div>
        )}

        {/* Sort/SWIFT Code */}
        {Boolean(swiftCode) && (
          <div className="flex items-center justify-between">
            <span className="text-[#667085]">Sort / SWIFT Code</span>
            <span className="font-medium text-[#101828]">{swiftCode}</span>
          </div>
        )}

        {/* Routing number */}
        {Boolean(routingNumber) && (
          <div className="flex items-center justify-between">
            <span className="text-[#667085]">Routing number</span>
            <span className="font-medium text-[#101828]">{routingNumber}</span>
          </div>
        )}

        {/* IBAN number */}
        {Boolean(iban) && (
          <div className="flex items-center justify-between">
            <span className="text-[#667085]">IBAN</span>
            <span className="font-medium text-[#101828]">{iban}</span>
          </div>
        )}

        {/* Date */}
        <div className="flex items-center justify-between">
          <span className="text-[#667085]">Date</span>
          <span className="font-medium text-[#101828]">{request.date}</span>
        </div>
      </div>
    </div>
  );
};
